import { webPlayerStore } from "@app/Screens/WebPlayer/store/webPlayerStore";
import { FxIdDomainSettingsEGameOrientation } from "@app/Api/gen";
import { BackgroundImage, Box } from "@mantine/core";
import React from "react";
import { GameBackgroundProps } from "@app/Screens/WebPlayer/index";

export const GameBackground = (props: GameBackgroundProps) => {
	const gameConfig = webPlayerStore((state) => state.gameConfig);
	const { children, gameWidth, gameHeight } = props;

	// Ensure PublicWebClientConfig and Background exist
	if (!gameConfig?.PublicWebClientConfig?.Background) {
		log.warn("GameBackground: Missing PublicWebClientConfig or Background configuration.");
		return <>{children}</>;
	}

	if (gameWidth == 0 || gameHeight == 0) {
		log.info("GameBackground: Missing Width or Height.");
		return <>{children}</>;
	}

	const backgroundConfig = gameConfig.PublicWebClientConfig.Background;
	const gameOrientation = gameConfig.GameOrientation; // Still assuming this exists

	// Destructure properties from backgroundConfig
	const { Type, Image, SideBySide } = backgroundConfig;

	// Handle the new "side-by-side" type
	if (Type === "side-by-side") {
		if (gameOrientation !== FxIdDomainSettingsEGameOrientation.Vertical) {
			log.warn(
				'GameBackground: "side-by-side" background type requires GameOrientation to be "vertical". Falling back to no background.'
			);
			return <>{children}</>;
		}

		// Check if SideBySide object and its properties exist
		if (!SideBySide?.LeftImage || !SideBySide?.RightImage) {
			log.warn(
				'GameBackground: "side-by-side" background type requires "SideBySide.LeftImage" and "SideBySide.RightImage" image URLs in config. Falling back to no background.'
			);
			return <>{children}</>;
		}

		// Define base styles for side images
		const sideImageBaseStyles = {
			flex: "1 1 0", // Allow grow, allow shrink, base size 0 (will take remaining space)
			height: "100%",
			backgroundSize: "cover",
			backgroundRepeat: "no-repeat"
		};

		// TODO: Determine aspect ratio dynamically from gameConfig if possible
		const verticalGameAspectRatio = gameWidth / gameHeight; // Example: 9:16

		log.info("verticalGameAspectRatio", verticalGameAspectRatio, gameWidth, gameHeight);

		return (
			// Main flex container: centers items horizontally
			<Box
				sx={{ display: "flex", width: "100%", height: "100%", alignItems: "stretch", justifyContent: "center" }}
			>
				{/* Left Side Image */}
				<Box
					sx={{
						...sideImageBaseStyles,
						backgroundImage: `url(${SideBySide.LeftImage})`,
						backgroundPosition: "center right" // Show right edge of left image
					}}
				/>

				{/* Game Content Wrapper */}
				{/* This Box controls the size and aspect ratio of the game area */}
				<Box
					sx={{
						flex: "0 0 auto", // Don't grow or shrink, use calculated size
						height: "100%", // Take full available height
						position: "relative", // For potential absolute positioning of children if needed
						aspectRatio: `${verticalGameAspectRatio}`, // Maintain aspect ratio (width calculated from height)
						maxWidth: "100%", // Ensure it doesn't overflow horizontally
						overflow: "hidden" // Hide potential overflow from children
					}}
				>
					{/* Render children (game-container) inside the sized wrapper */}
					{/* Ensure the child component fills this wrapper */}
					{React.isValidElement(children)
						? React.cloneElement(children as React.ReactElement, {
								// Force the child (game-container) to fill this wrapper
								style: { width: "100%", height: "100%", ...children.props.style }
							})
						: children}
				</Box>

				{/* Right Side Image */}
				<Box
					sx={{
						...sideImageBaseStyles,
						backgroundImage: `url(${SideBySide.RightImage})`,
						backgroundPosition: "center left" // Show left edge of right image
					}}
				/>
			</Box>
		);
	}

	// Handle existing types ("fullscreen", "tiling", "centered")
	// ... (rest of the component remains the same) ...
	if (Type === "fullscreen" || Type === "tiling" || Type === "centered") {
		let backgroundStyles = {};
		switch (Type) {
			case "fullscreen":
				backgroundStyles = {
					backgroundRepeat: "no-repeat",
					backgroundSize: "cover",
					backgroundPosition: "center"
				};
				break;
			case "tiling":
				backgroundStyles = {
					backgroundRepeat: "repeat",
					backgroundSize: "auto"
				};
				break;
			case "centered":
				backgroundStyles = {
					backgroundSize: "contain",
					backgroundPosition: "center center",
					backgroundRepeat: "no-repeat"
				};
				break;
		}

		// Check if the main 'Image' URL exists for these types
		if (!Image) {
			log.warn(
				`GameBackground: Background type "${Type}" requires a top-level "Image" URL in config. Falling back to no background.`
			);
			return <>{children}</>;
		}

		return (
			<BackgroundImage
				src={Image} // Use the top-level Image property
				sx={{
					height: "100%",
					width: "100%",
					...backgroundStyles
				}}
			>
				{children}
			</BackgroundImage>
		);
	}

	// Fallback for unknown types or if config is somehow invalid
	log.warn(`GameBackground: Unknown or invalid background type "${Type}". Rendering without background.`);
	return <>{children}</>;
};
